import {
  Button,
  Dialog,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Box, Hide, HStack, Stack, Text, VStack} from 'platform/foundation';

import {useFormState, useWatch} from 'react-hook-form';

import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {TestIdProps, getSafely, suffixTestId} from 'shared/web';

import {useApiEnums} from '../../../hooks/useApiEnums';
import {useFeatureLabels} from '../../../hooks/useFeatureLabels';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {getPresetTags} from '../utils/getPresetTags';
import {
  PresetEditorFormValues,
  buildPresetEditorDefaultValues,
  getRelativeFilterConfigFromPresetEditorForm,
  presetEditorFormSchema,
} from '../utils/presetEditorFormSchema';
import {BoundariesSection} from './BoundariesSection';
import {PresetFeaturesField} from './PresetFeaturesField';
import {PropertiesSection} from './PropertiesSection';

interface PresetEditorDialogProps extends TestIdProps {
  isOpen: boolean;
  /** null means create mode */
  initialPreset: RelativeFilterResponseBody | null;
  onSave: (name: string, config: RelativeFilterConfigBody, isDefault: boolean) => void;
  onDelete?: () => void;
  onClose: () => void;
}

type PresetEditorDialogFormBodyProps = PresetEditorDialogProps & {
  control: FormControl<PresetEditorFormValues>;
  isConfigReadOnly: boolean;
  isSystemPreset: boolean;
};

const PRESET_SUMMARY_CONFIG_WATCH_NAMES = [
  'year',
  'mileage',
  'mileagePct',
  'power',
  'powerPct',
  'price',
  'pricePct',
  'featureScore',
  'trimLevel',
  'transmission',
  'fuel',
  'body',
  'drive',
  'vatDeductible',
  'sellerType',
  'seats',
  'radius',
  'features',
] as const;

function PresetEditorDialogFormBody(props: PresetEditorDialogFormBodyProps) {
  const {control, isConfigReadOnly, isSystemPreset} = props;
  const sourcingCurrency = useSourcingCurrency();
  const {getEnum} = useApiEnums();
  const {getFeatureLabel, isKnownFeature} = useFeatureLabels();

  const [
    wYear,
    wMileage,
    wMileagePct,
    wPower,
    wPowerPct,
    wPrice,
    wPricePct,
    wFeatureScore,
    wTrimLevel,
    wTransmission,
    wFuel,
    wBody,
    wDrive,
    wVatDeductible,
    wSellerType,
    wSeats,
    wRadius,
    wFeatures,
  ] = useWatch({
    control,
    name: [...PRESET_SUMMARY_CONFIG_WATCH_NAMES],
  });

  const summaryConfig: RelativeFilterConfigBody = {
    year: wYear ?? null,
    mileage: wMileage ?? null,
    mileagePct: wMileagePct ?? null,
    power: wPower ?? null,
    powerPct: wPowerPct ?? null,
    price: wPrice ?? null,
    pricePct: wPricePct ?? null,
    featureScore: wFeatureScore ?? null,
    trimLevel: wTrimLevel ?? null,
    transmission: wTransmission ?? null,
    fuel: wFuel ?? null,
    body: wBody ?? null,
    drive: wDrive ?? null,
    vatDeductible: wVatDeductible ?? null,
    sellerType: wSellerType ?? null,
    seats: wSeats ?? null,
    radius: wRadius ?? null,
    features: wFeatures ?? null,
  };

  const {isValid} = useFormState<PresetEditorFormValues>();
  const sellerTypeLabels = Object.fromEntries(
    getEnum('seller_type').map((item) => [item.key, item.label])
  );
  const summaryTags = getPresetTags(summaryConfig, {
    currencyCode: sourcingCurrency?.code,
    getSellerTypeLabel: (key) => getSafely(sellerTypeLabels, key, key),
    getFeatureLabel,
    isKnownFeature,
  });

  return (
    <>
      <VStack spacing={4}>
        <Stack direction={['column', 'row']} align="flex-start" justify="space-between" spacing={4}>
          <Box width={68} flexShrink={0}>
            <FormField
              control={control}
              name="name"
              type="text"
              label={i18n.t('entity.sourcing.relativeFilters.dialog.presetName')}
              isRequired
              isDisabled={isConfigReadOnly}
              data-testid={suffixTestId('nameInput', props)}
            />
          </Box>
          {summaryTags.length > 0 ? (
            <HStack spacing={1} justify={[undefined, 'flex-end']} wrap>
              {summaryTags.map((tag) => (
                <Flag key={tag.id} label={tag.label} size="small" colorScheme="blue" isSubtle />
              ))}
            </HStack>
          ) : (
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.sourcing.relativeFilters.summary.noConstraints')}
            </Text>
          )}
        </Stack>

        <Separator spacing={0} />

        <Stack direction={['column', 'row']} spacing={[4, 0]}>
          <Box flexGrow={1}>
            <BoundariesSection isReadOnly={isConfigReadOnly} data-testid={props['data-testid']} />
          </Box>
          <Separator orientation="vertical" spacing={4} />
          <Box flexGrow={1}>
            <PropertiesSection isReadOnly={isConfigReadOnly} />
          </Box>
        </Stack>

        <PresetFeaturesField isReadOnly={isConfigReadOnly} data-testid={props['data-testid']} />
      </VStack>

      <DialogFooter data-testid={suffixTestId('footer', props)}>
        <HStack justify="space-between" align="center">
          <HStack spacing={3} align="center">
            <Hide when={isSystemPreset || !props.onDelete}>
              <Button
                title={i18n.t('entity.sourcing.relativeFilters.dialog.delete')}
                leftIcon="action/delete_outline"
                variant="dangerOutlined"
                type="button"
                onClick={props.onDelete}
                data-testid={suffixTestId('deleteButton', props)}
              />
            </Hide>
            <FormField
              control={control}
              name="isDefault"
              type="checkbox"
              label={i18n.t('entity.sourcing.relativeFilters.dialog.setAsDefault')}
              data-testid={suffixTestId('setAsDefaultCheckbox', props)}
            />
          </HStack>
          <HStack spacing={2} justify="flex-end">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              type="button"
              onClick={props.onClose}
              data-testid={suffixTestId('cancelButton', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              isDisabled={!isValid}
              data-testid={suffixTestId('saveButton', props)}
            />
          </HStack>
        </HStack>
      </DialogFooter>
    </>
  );
}

export function PresetEditorDialog(props: PresetEditorDialogProps) {
  const isSystemPreset = props.initialPreset?.system ?? false;
  const isConfigReadOnly = isSystemPreset;

  const defaultValues = buildPresetEditorDefaultValues(props.initialPreset);

  const onSubmit: FormSubmitHandler<PresetEditorFormValues> = (values) => {
    props.onSave(
      values.name.trim(),
      getRelativeFilterConfigFromPresetEditorForm(values),
      values.isDefault
    );
    return Promise.resolve();
  };

  return (
    <Dialog
      title={i18n.t('entity.sourcing.relativeFilters.dialog.title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      withAdditionalFooter
      scrollBehavior="inside"
      data-testid={suffixTestId('dialog', props)}
    >
      <Form<PresetEditorFormValues>
        experimentalZodSchema={presetEditorFormSchema}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        mode="onChange"
      >
        {(control) => (
          <PresetEditorDialogFormBody
            control={control}
            isConfigReadOnly={isConfigReadOnly}
            isSystemPreset={isSystemPreset}
            {...props}
          />
        )}
      </Form>
    </Dialog>
  );
}
